import React from 'react';
import type { OrderSummary } from '../../../../infrastructure/OMSClient';
import { ShippingProviderType } from '../../../../infrastructure/OMSClient';
import { getStatusTextColor, getStatusBgColor } from '../utils/statusColor';
import { Link } from 'react-router-dom';
import { getConfig } from '../../../../config';
import { formatPicLabel } from '../utils/formatPicLabel';
import { fulfillmentSourceLabel } from '@/utils/fulfillmentSource';
import { purchaseOrderUrl } from '@/utils/fulfillmentPurchaseOrder';

export interface Column {
  accessor: keyof OrderSummary | string;
  header: string;
  cell?: (order: OrderSummary) => React.ReactNode;
  isVisible?: boolean;
  isRequired?: boolean;
}

export const Columns: Column[] = [
  {
    accessor: 'orderId',
    header: 'Order ID',
    isRequired: true,
    isVisible: true,
    cell: (order: OrderSummary) => {
      const fromOrders = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      return (
        <Link
          to={`/oms/orders/${order.orderId}`}
          state={{ fromOrders }}
          className="text-primary text-decoration-none fw-medium"
          onClick={(e) => e.stopPropagation()}
        >
          {order.orderId}
        </Link>
      );
    },
  },
  {
    accessor: 'orderDate',
    header: 'Order Date',
    isRequired: true,
    isVisible: true,
    cell: (order: OrderSummary) => {
      const date = new Date(order.orderDate);
      return date.toISOString().split('T')[0];
    },
  },
  {
    accessor: 'countryCode',
    header: 'Country',
    isVisible: true,
  },
  {
    accessor: 'status',
    header: 'Status',
    isVisible: true,
    cell: (order: OrderSummary) => {
      const status = order.status;
      return (
        <span
          className="text-sm font-medium"
          style={{
            color: getStatusTextColor(order.status),
            backgroundColor: getStatusBgColor(order.status),
            padding: '0.25rem 0.5rem',
            borderRadius: '0.25rem',
            display: 'inline-block',
          }}
        >
          {status && status[0]?.toUpperCase() + status.slice(1)}
        </span>
      );
    },
  },
  {
    accessor: 'shippingProvider',
    header: 'Provider',
    isVisible: true,
    cell: (order: OrderSummary) => {
      if (order.shippingData?.provider === ShippingProviderType.PARXL || order.parxlData) {
        return (
          <span
            className="text-sm font-medium"
            style={{
              backgroundColor: '#17a2b8',
              color: 'white',
              padding: '0.2rem 0.5rem',
              borderRadius: '0.25rem',
            }}
          >
            PARXL
          </span>
        );
      }
      if (order.shippingData?.provider === ShippingProviderType.FEDEX) {
        return (
          <span
            className="text-sm font-medium"
            style={{
              backgroundColor: '#4d148c',
              color: 'white',
              padding: '0.2rem 0.5rem',
              borderRadius: '0.25rem',
            }}
          >
            FEDEX
          </span>
        );
      }
      if (order.shippingData?.provider === ShippingProviderType.YUN) {
        return (
          <span
            className="text-sm font-medium"
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              padding: '0.2rem 0.5rem',
              borderRadius: '0.25rem',
            }}
          >
            YUN
          </span>
        );
      }
      const hasYUN = order.deleoData?.orderTrackingList?.some((tracking) =>
        tracking.deliveryCompanyCd?.name?.toUpperCase().includes('YUN'),
      );
      if (hasYUN) {
        return (
          <span
            className="text-sm font-medium"
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              padding: '0.2rem 0.5rem',
              borderRadius: '0.25rem',
            }}
          >
            YUN
          </span>
        );
      }
      if (order.shippingData?.provider === ShippingProviderType.DELEO || order.deleoData?.deleoOrderId) {
        return (
          <span
            className="text-sm font-medium"
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              padding: '0.2rem 0.5rem',
              borderRadius: '0.25rem',
            }}
          >
            DELEO
          </span>
        );
      }
      return <span className="text-muted">-</span>;
    },
  },
  {
    accessor: 'providerOrderId',
    header: 'Provider Order ID',
    isVisible: true,
    cell: (order: OrderSummary) => {
      const parxlRef = order.parxlData?.plsShipmentReference || order.shippingData?.providerOrderId;
      const deleoId = order.deleoData?.deleoOrderId;
      const yunTracking = order.deleoData?.orderTrackingList?.find((tracking) =>
        tracking.deliveryCompanyCd?.name?.toUpperCase().includes('YUN'),
      );
      const yunTrackingNo = yunTracking?.trackingNo;
      const displayId = parxlRef || yunTrackingNo || deleoId;
      const isParxl = order.shippingData?.provider === ShippingProviderType.PARXL || order.parxlData;
      const isFedEx = order.shippingData?.provider === ShippingProviderType.FEDEX;
      const isYUN = order.shippingData?.provider === ShippingProviderType.YUN || Boolean(yunTrackingNo);

      if (!displayId) {
        return <span className="text-muted">-</span>;
      }

      return (
        <span
          className="text-sm font-medium"
          style={{ color: isParxl ? '#17a2b8' : isFedEx ? '#4d148c' : isYUN ? '#28a745' : 'blue' }}
        >
          {displayId}
        </span>
      );
    },
  },
  {
    accessor: 'pic',
    header: 'PIC',
    isVisible: true,
    cell: (order: OrderSummary) => {
      return <span className="text-sm font-medium">{formatPicLabel(order)}</span>;
    },
  },
  {
    accessor: 'customer',
    header: 'Customer',
    isVisible: true,
    cell: (order: OrderSummary) => (
      <div>
        <div className="text-wrap">{order.customer.name}</div>
        <div className="text-muted small">{order.customer.email}</div>
      </div>
    ),
  },
  {
    accessor: 'consignee',
    header: 'Shipping Address',
    isVisible: true,
    cell: (order: OrderSummary) => {
      const address = order.shippingAddress;
      const consignee = order.consignee;
      const pick = (...values: Array<string | undefined>) => values.find((value) => value?.trim())?.trim();
      const text = [
        pick(address?.address1, consignee?.address),
        pick(address?.address2),
        pick(address?.city, consignee?.city),
        pick(address?.state, consignee?.state),
        pick(address?.postcode, consignee?.zipcode),
        pick(address?.country, consignee?.countryCode),
      ]
        .filter(Boolean)
        .join(', ');

      return (
        <div style={{ minWidth: '160px' }}>
          <div className="text-wrap text-muted small">{text || '-'}</div>
        </div>
      );
    },
  },
  {
    accessor: 'totalAmount',
    header: 'Total Value (Discount)',
    isVisible: true,
    cell: (order: OrderSummary) => (
      <div>
        <div className="text-wrap">{`${order.currency} ${order.totalAmount}`}</div>
        <div className="text-muted small">{`(${order.discount?.toFixed(2)})`}</div>
      </div>
    ),
  },
  {
    accessor: 'lineItems',
    header: 'Total Items',
    isVisible: true,
    cell: (order: OrderSummary) => order.lineItems.length,
  },
  {
    accessor: 'lineItems.productNameKr',
    header: 'Product Name KR',
    isVisible: true,
    cell: (order: OrderSummary) => {
      if (!order.lineItems?.length) {
        return <span className="text-muted">-</span>;
      }
      // One entry per line item, row-aligned with the SKU column so cart orders
      // (many items in one order) can be matched to their order number by KR name.
      return (
        <div style={{ width: '220px' }}>
          {order.lineItems.map((item) => {
            const nameKr = item.nameKor || item.name;
            return (
              <div key={item.itemId || item.sku} className="mb-1" style={{ fontSize: '0.75rem' }}>
                <div className="font-medium">{nameKr}</div>
                {item.originalNameKor && <div style={{ color: '#6b7280' }}>({item.originalNameKor}*)</div>}
              </div>
            );
          })}
        </div>
      );
    },
  },
  {
    accessor: 'lineItems.sourcingInfo',
    header: 'SKU (Sourcing Route/ Order Number/ Tracking Number)',
    isVisible: true,
    cell: (order: OrderSummary) => {
      const getAdminBaseUrl = () => {
        const config = getConfig();
        return config.adminUrl;
      };
      const items = order.lineItems
        .map((item) => {
          const sku = item.sku;
          const orderNumber = item.sourcingShipmentTrack?.orderNumber;
          const trackingNumber = item.sourcingShipmentTrack?.trackingNumber;
          const shipmentTrackUrl = item.sourcingShipmentTrack?.shipmentTrackUrl;
          const sourcingRoute = item.sourcingRoute;
          const skuLink = `${getAdminBaseUrl()}/catalog/ss-editor?sku=${sku}`;
          const isInventory = sourcingRoute?.toLowerCase() === 'existing inventory';

          const renderOrderNumber = () => {
            if (isInventory) {
              return null;
            }
            if (!orderNumber) {
              return 'Not ordered';
            }
            const orderHref = purchaseOrderUrl(orderNumber, item.purchasePlatform);
            if (orderHref) {
              return (
                <a
                  href={orderHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {orderNumber}
                </a>
              );
            }
            return orderNumber;
          };

          const renderTrackingNumber = () => {
            if (isInventory) {
              return null;
            }
            if (!trackingNumber) {
              const preparingText = orderNumber ? 'Preparing' : 'NA';
              if (preparingText === 'Preparing' && shipmentTrackUrl) {
                return (
                  <a
                    href={shipmentTrackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Preparing
                  </a>
                );
              }
              return preparingText;
            }
            if (shipmentTrackUrl) {
              return (
                <a
                  href={shipmentTrackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {trackingNumber}
                </a>
              );
            }
            return trackingNumber;
          };

          const sourcingRouteDisplay = sourcingRoute ? fulfillmentSourceLabel(sourcingRoute) : '-';
          const orderNumberDisplay = renderOrderNumber();
          const trackingNumberDisplay = renderTrackingNumber();
          const isNotOrdered = !orderNumber && (sourcingRouteDisplay === '-' || !sourcingRoute);
          const isInventoryDisplay = sourcingRouteDisplay?.toLowerCase() === 'existing inventory';
          const shouldHideOrderAndTracking = isInventory || isInventoryDisplay || isNotOrdered;

          return (
            <div key={item.itemId || sku} style={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
              <a
                href={skuLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {sku}
              </a>
              {` (`}
              {sourcingRouteDisplay}
              {!shouldHideOrderAndTracking && orderNumberDisplay !== null && (
                <>
                  {` / `}
                  {orderNumberDisplay}
                </>
              )}
              {!shouldHideOrderAndTracking && trackingNumberDisplay !== null && (
                <>
                  {` / `}
                  {trackingNumberDisplay}
                </>
              )}
              {`)`}
            </div>
          );
        })
        .filter(Boolean);
      return items.length > 0 ? (
        <div className="text-xs" style={{ width: '360px' }}>
          {items}
        </div>
      ) : (
        <span className="text-muted">-</span>
      );
    },
  },
  {
    accessor: 'trackingNo',
    header: 'Tracking No',
    isVisible: true,
    cell: (order: OrderSummary) => {
      const yunTracking = order.deleoData?.orderTrackingList?.find((tracking) =>
        tracking.deliveryCompanyCd?.name?.toUpperCase().includes('YUN'),
      );
      const trackingNo =
        order.shippingData?.trackingNumber ||
        (order.shippingData?.provider === ShippingProviderType.YUN ||
        order.shippingData?.provider === ShippingProviderType.FEDEX
          ? order.shippingData?.providerOrderId
          : undefined) ||
        yunTracking?.trackingNo ||
        order.deleoData?.deleoTrackingNo;
      return trackingNo || '-';
    },
  },
];
